/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessage, FormattedMessage} from 'react-intl'
import {useItemVariant} from '../../../components/item-variant'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {CloseIcon} from '@chakra-ui/icons'
import {useProductsChunked} from '../../../hooks/use-products-chunked'

export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item from your cart?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

const CartAddToWishlistButton = ({onAddToWishlistClick = noop}) => {
    const variant = useItemVariant()
    const {data: customer} = useCurrentCustomer()

    // Cart line items are variant SKUs, which don't carry `primaryCategoryId`.
    // Resolve the variation group id and fetch it (lean select) to get the category.
    const variationGroupId = variant?.variationGroups?.[0]?.productId
    const {data: groupProducts} = useProductsChunked(variationGroupId, {
        enabled: Boolean(variationGroupId),
        staleTime: 5 * 60 * 1000,
        parameters: {select: '(data.(id,primaryCategoryId))'}
    })
    const primaryCategoryId = groupProducts?.[variationGroupId]?.primaryCategoryId

    return (
        customer.isRegistered && !(variant?.c_isGiftCard || variant?.c_isEGiftCard) && (
            <Button
                variant="link"
                size="xs"
                textDecoration="underline"
                onClick={() => onAddToWishlistClick(variant, primaryCategoryId)}
                data-testid="cart-move-to-wishlist"
            >
                <FormattedMessage
                    defaultMessage="Move to Wishlist"
                    id="cart_secondary_button_group.action.added_to_wishlist"
                />
            </Button>
        )
    )
}

CartAddToWishlistButton.propTypes = {
    onAddToWishlistClick: PropTypes.func
}

const CartRemoveButton = ({cartRemoveToast = noop, productName}) => {
    return (
        <Button variant="link" size="sm" onClick={cartRemoveToast}
            aria-label={productName ? `Remove ${productName} product from cart` : 'Remove item from cart'}>
            <CloseIcon boxSize={3} aria-hidden="true" focusable="false" />
        </Button>
    )
}

CartRemoveButton.propTypes = {
    cartRemoveToast: PropTypes.func
}

const CartEditButton = ({onEditClick = noop, productName}) => {
    const variant = useItemVariant()

    return (
        <Button
            variant="link"
            size="xs"
            textDecoration="underline"
            onClick={() => onEditClick(variant)}
            data-testid="cart-edit"
            aria-label={productName ? `Open modal to edit product ${productName}` : 'Edit button'}
        >
            <FormattedMessage defaultMessage="Edit" id="cart_secondary_button_group.action.edit" />
        </Button>
    )
}

CartEditButton.propTypes = {
    onEditClick: PropTypes.func
}

// Default export for compatibility with base template imports
const CartSecondaryButtonGroup = () => null

export {CartAddToWishlistButton, CartRemoveButton, CartEditButton}
export default CartSecondaryButtonGroup
